import practicalTestService from 'ApiService/Student/practicalTestService';
import Card from 'components/card';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

const Index = () => {
  const { id } = useParams();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);

  useEffect(() => {
    fetchTests();
  }, []);

  useEffect(() => {
    if (id && tests.length > 0) {
      const match = tests.find((t) => t._id === id);
      if (match) setSelectedTest(match);
    }
  }, [id, tests]);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const response = await practicalTestService.getAssignedTests();
      if (response.data.success) {
        setTests(response.data.data);
      } else {
        setTests([]);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch practical tests.');
      }
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr, timeStr) => {
    const d = new Date(dateStr);
    return `${d.toLocaleDateString()} ${timeStr}`;
  };

  const getStatus = (test) => {
    const now = new Date();
    const start = new Date(test.startDate);
    const [sh, sm] = test.startTime.split(':');
    start.setUTCHours(0, 0, 0, 0);
    start.setTime(start.getTime() + (parseInt(sh) * 60 + parseInt(sm)) * 60000 - 5.5 * 3600000);
    const end = new Date(test.endDate);
    const [eh, em] = test.endTime.split(':');
    end.setUTCHours(0, 0, 0, 0);
    end.setTime(end.getTime() + (parseInt(eh) * 60 + parseInt(em)) * 60000 - 5.5 * 3600000);

    if (now < start) return 'upcoming';
    if (now > end) return test.submission ? 'submitted' : 'expired';
    return 'active';
  };

  if (selectedTest) {
    return (
      <TakeTest
        test={selectedTest}
        onBack={() => setSelectedTest(null)}
        onSubmitted={fetchTests}
      />
    );
  }

  return (
    <div className="mt-3 h-full w-full px-4">
      <Card extra="w-full p-6">
        <h3 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Practical Tests
        </h3>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : tests.length === 0 ? (
          <p className="text-gray-500">No practical tests assigned to you.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {tests.map((test) => {
              const status = getStatus(test);
              const statusColors = {
                active: 'bg-green-100 text-green-700',
                submitted: 'bg-blue-100 text-blue-700',
                upcoming: 'bg-yellow-100 text-yellow-700',
                expired: 'bg-gray-100 text-gray-500',
              };
              return (
                <div
                  key={test._id}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-navy-600 dark:bg-navy-800"
                >
                  <div className="flex items-start justify-between">
                    <h4 className="text-lg font-semibold text-navy-700 dark:text-white">
                      {test.title}
                    </h4>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${statusColors[status]}`}>
                      {status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Start: {formatDateTime(test.startDate, test.startTime)}
                  </p>
                  <p className="text-sm text-gray-500">
                    End: {formatDateTime(test.endDate, test.endTime)}
                  </p>
                  {status === 'active' && !test.submission && (
                    <button
                      onClick={() => setSelectedTest(test)}
                      className="mt-3 w-full rounded-lg bg-brand-900 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
                    >
                      Take Test
                    </button>
                  )}
                  {test.submission && (
                    <button
                      onClick={() => setSelectedTest(test)}
                      className="mt-3 w-full rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600"
                    >
                      {status === 'active' ? 'Edit Submission' : 'View Submission'}
                    </button>
                  )}
                  {status === 'upcoming' && (
                    <p className="mt-2 text-center text-xs text-yellow-600">Not started yet</p>
                  )}
                  {status === 'expired' && !test.submission && (
                    <p className="mt-2 text-center text-xs text-red-500">Test has ended</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

const TakeTest = ({ test, onBack, onSubmitted }) => {
  const [githubLink, setGithubLink] = useState(test.submission?.githubLink || '');
  const [codeZip, setCodeZip] = useState(null);
  const [driveLink, setDriveLink] = useState(test.submission?.driveLink || '');
  const [submitting, setSubmitting] = useState(false);
  const [requestingPermission, setRequestingPermission] = useState(false);
  const [localPermStatus, setLocalPermStatus] = useState(test.submission?.driveLinkPermissionStatus || 'none');
  const [localRequestedAt, setLocalRequestedAt] = useState(test.submission?.driveLinkPermissionRequestedAt || null);

  const handleDownload = async (url, name) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download file.');
    }
  };

  const isExpired = () => {
    const now = new Date();
    const end = new Date(test.endDate);
    const [eh, em] = test.endTime.split(':');
    end.setUTCHours(0, 0, 0, 0);
    end.setTime(end.getTime() + (parseInt(eh) * 60 + parseInt(em)) * 60000 - 5.5 * 3600000);
    return now > end;
  };

  const handleSubmit = async () => {
    if (!githubLink.trim()) {
      toast.error('GitHub link is required.');
      return;
    }
    if (!test.submission && !codeZip) {
      toast.error('Submission file is required.');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('testId', test._id);
      formData.append('githubLink', githubLink);
      formData.append('driveLink', driveLink);
      if (codeZip) {
        formData.append('codeZip', codeZip);
      }

      const response = await practicalTestService.submitTest(formData);
      if (response.data.success) {
        toast.success('Test submitted successfully!');
        onSubmitted();
        onBack();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestPermission = async () => {
    setRequestingPermission(true);
    try {
      const response = await practicalTestService.requestDriveLinkPermission(test._id);
      if (response.data.success) {
        toast.success('Permission request sent to teacher.');
        setLocalPermStatus('requested');
        setLocalRequestedAt(new Date().toISOString());
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Request failed.');
    } finally {
      setRequestingPermission(false);
    }
  };

  const handleUploadDriveLink = async () => {
    if (!driveLink.trim()) {
      toast.error('Please enter a drive link.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await practicalTestService.uploadDriveLink(test._id, driveLink);
      if (response.data.success) {
        toast.success('Drive link uploaded successfully.');
        onSubmitted();
        onBack();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const canEdit = !isExpired();
  const canSubmit = canEdit;
  const needsDrivePermission = test.submission && !test.submission.driveLink && localPermStatus === 'none';

  const canRequestAgain = () => {
    if (!localRequestedAt) return true;
    const hoursPassed = (Date.now() - new Date(localRequestedAt).getTime()) / (1000 * 60 * 60);
    return hoursPassed >= 24;
  };

  const permissionRequested = localPermStatus === 'requested';
  const permissionApproved = localPermStatus === 'approved';
  const permissionDenied = localPermStatus === 'denied';

  return (
    <div className="mt-3 h-full w-full px-4">
      <Card extra="w-full p-6">
        <button
          onClick={onBack}
          className="mb-4 cursor-pointer rounded-xl bg-lightPrimary px-6 py-2 text-sm font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
        >
          Back to Tests
        </button>

        <h3 className="mb-2 text-xl font-bold text-navy-700 dark:text-white">{test.title}</h3>

        {test.contentType === 'typed' && test.description && (
          <div className="mb-4 rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
            <p className="whitespace-pre-wrap text-sm text-navy-700 dark:text-white">{test.description}</p>
          </div>
        )}

        {test.contentType !== 'typed' && test.fileFileName && (
          <div className="mb-4 space-y-1">
            <p className="text-sm font-medium text-gray-500">Test files:</p>
            {(() => {
              const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001/api/v1';
              const paths = Array.isArray(test.fileFilePath) ? test.fileFilePath : (test.fileFilePath ? [test.fileFilePath] : []);
              const names = Array.isArray(test.fileFileName) ? test.fileFileName : (test.fileFileName ? [test.fileFileName] : []);
              return paths.map((p, i) => {
                const url = `${base}/${p.replace(/\\/g, '/')}`;
                const name = names[i] || p.split('/').pop();
                const ext = name.split('.').pop().toLowerCase();
                const isDownloadOnly = ext === 'csv' || ext === 'xlsx' || ext === 'pkt';
                return (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">{name}</span>
                    {isDownloadOnly ? (
                      <button
                        onClick={() => handleDownload(url, name)}
                        className="cursor-pointer text-sm text-brand-600 underline"
                      >
                        Download
                      </button>
                    ) : (
                      <>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-brand-600 underline"
                        >
                          View
                        </a>
                        <button
                          onClick={() => handleDownload(url, name)}
                          className="cursor-pointer text-sm text-brand-600 underline"
                        >
                          Download
                        </button>
                      </>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}

        <div className="mb-6 border-t border-gray-200 pt-4 dark:border-navy-600">
          <h4 className="mb-3 font-semibold text-navy-700 dark:text-white">Your Submission</h4>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              GitHub Link <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={githubLink}
              onChange={(e) => setGithubLink(e.target.value)}
              placeholder="https://github.com/your-username/your-repo"
              disabled={!canEdit}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
          </div>

          {canEdit && (
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                Submission File {!test.submission && <span className="text-red-500">*</span>}
              </label>
              <input
                type="file"
                onChange={(e) => setCodeZip(e.target.files[0])}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
              {codeZip && <p className="mt-1 text-xs text-green-600">Selected: {codeZip.name}</p>}
              {test.submission?.codeFileName && !codeZip && (
                <p className="mt-1 text-xs text-gray-500">Current file: {test.submission.codeFileName}</p>
              )}
            </div>
          )}

          {!canEdit && test.submission?.codeFileName && (
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                Submitted ZIP
              </label>
              <p className="text-sm text-gray-600">{test.submission.codeFileName}</p>
            </div>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Drive Link 
            </label>
            <input
              type="url"
              value={driveLink}
              onChange={(e) => setDriveLink(e.target.value)}
              placeholder="https://drive.google.com/your-video-link"
              disabled={!canEdit && !permissionApproved}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
            {test.submission?.driveLink && (
              <p className="mt-1 text-xs text-green-600">
                Drive link submitted: {test.submission.driveLink}
              </p>
            )}
          </div>

          {test.submission && (
            <div className="mb-4 rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
              {test.submission.marks !== null && test.submission.marks !== undefined ? (
                <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                  Marks: {test.submission.marks} / {test.outOf ?? '—'}
                </p>
              ) : (
                <p className="text-sm text-gray-500">Not graded yet.</p>
              )}
            </div>
          )}

          {canSubmit && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full rounded-xl bg-brand-900 px-6 py-3 font-medium text-white transition hover:bg-brand-800 disabled:opacity-60"
            >
              {submitting
                ? test.submission ? 'Updating...' : 'Submitting...'
                : test.submission ? 'Update Submission' : 'Submit Test'}
            </button>
          )}

          {needsDrivePermission && canRequestAgain() && (
            <button
              onClick={handleRequestPermission}
              disabled={requestingPermission}
              className="w-full rounded-xl bg-yellow-500 px-6 py-3 font-medium text-white transition hover:bg-yellow-600 disabled:opacity-60"
            >
              {requestingPermission ? 'Requesting...' : 'Request Permission for Drive Link'}
            </button>
          )}

          {permissionRequested && (
            <p className="mt-2 text-center text-sm text-yellow-600">
              {canRequestAgain()
                ? 'Permission requested. Waiting for teacher approval.'
                : 'Request already sent. You can request again after 24 hours.'}
            </p>
          )}

          {permissionApproved && (
            <button
              onClick={handleUploadDriveLink}
              disabled={submitting || !driveLink.trim()}
              className="w-full rounded-xl bg-green-600 px-6 py-3 font-medium text-white transition hover:bg-green-700 disabled:opacity-60"
            >
              {submitting ? 'Uploading...' : 'Upload Drive Link'}
            </button>
          )}

          {permissionDenied && (
            <p className="mt-2 text-center text-sm text-red-600">
              Drive link permission was denied by the teacher.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Index;
